import { LinkIcon, ViewIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Image,
  Modal,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { faTicketAlt } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Cookies from "js-cookie";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { BaseUrl } from "../../const.ts";
import { TicketContext, useContext } from "../../context/index.tsx";
import { useDetail } from "../../hooks/useDetail.ts";
import { InpersonIcon } from "../../icons/InpersonIcon.tsx";
import { OnlineIcon } from "../../icons/OnlineIcon.tsx";
import { Contact } from "./contact.tsx";
import { Tickets } from "./getTickets/tickets.tsx";
import { Hosted } from "./hosted.tsx";
import { Information } from "./information.tsx";
import { Invite } from "./invite.tsx";

const DetailView = () => {
  const { setCheckLogin } = useContext(TicketContext);
  const [token, setToken] = useState(false);
  const { t } = useTranslation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { data } = useDetail();

  useEffect(() => {
    const accessToken = Cookies.get("access");
    if (accessToken) {
      setToken(!token);
    }
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);


  return (
      <Box pt="100px">
        {/* Cover Image Section */}
        <Box w="100%" position="relative">
          <Image
              src={`${BaseUrl}/images/${data?.coverPhoto}`}
              alt="Hosted Cover"
              objectFit="contain" // important part
              width="100%"
              maxHeight={{ base: "300px", md: "500px" }} // limit max height
              borderRadius={{ base: "0", md: "20px" }}
              onClick={onOpen}
              cursor="pointer"
              backgroundColor="#f1f1f1" // optional, nice grey bg for transparent images
          />
        </Box>


        {/* Main Content Section */}
        <Box w="100%" borderRadius="20px" backgroundColor="white" mt={{ base: "20px", md: "40px" }}>
        <Box w="80%" margin="auto">
          <HStack
            flexDirection={{ base: "column", md: "row" }}
            p="30px 0px"
            spacing="20px"
            alignItems="center"
            borderBottom="1px solid #d4d4d4"
            justifyContent={{ base: "center", md: "space-between" }} // Centered on small screens, spaced on larger screens
            flexWrap="wrap" // Allows the items to wrap if the space is limited
          >
            <HStack spacing="10px">
              <Box
                  borderWidth={data?.ticketType === "OTHER" ? "1px" : "0"}
                  display="flex"
                  alignItems="center"
                  p="8px 20px"
                  backgroundColor={
                    data?.ticketType === "PAID"
                        ? "#C9B16B"
                        : data?.ticketType === "FREE"
                            ? "green.500"
                            : "white"
                  }
                  borderRadius="15px"
                  color={data?.ticketType === "OTHER" ? "#2485D0" : "white"}
                  fontWeight="bold"
                  fontSize="14px"
              >
                {data?.ticketType === "PAID" ? (
                    `${data?.ticketCost} ₼`
                ) : data?.ticketType === "OTHER" ? (
                    <Flex alignItems="center">
                      <LinkIcon mr={2} h="20px" w="20px" color="#2485D0" /> {t("OTHER")}
                    </Flex>
                ) : (
                    t("FREE")
                )}
              </Box>

              <Box
                display="flex"
                alignItems="center"
                p="8px 20px"
                backgroundColor="gray"
                borderRadius="15px"
                color="white"
                fontWeight="bold"
                fontSize="14px"
              >
                {data?.attendanceTypeEnum === "ONLINE" ? (
                  <OnlineIcon />
                ) : (
                  <InpersonIcon />
                )}
                <Box as="span" ml={2}>
                  {data?.attendanceTypeEnum === "ONLINE"
                    ? "Online"
                    : t("in_person")}
                </Box>
              </Box>
              <Box
                display="flex"
                alignItems="center"
                p="8px 20px"
                backgroundColor="#504a4b"
                borderRadius="15px"
                color="white"
                fontWeight="bold"
                fontSize="14px"
              >
                <ViewIcon width="16px" mr={4} h="16px" />
                <Text>{data?.viewCount}</Text>
              </Box>
            </HStack>

            <Heading
              fontSize={{ base: "20px", md: "28px" }} // Responsive font size
              fontWeight="600"
              textAlign={{ base: "center", md: "left" }} // Centered on small screens, left-aligned on larger screens
            >
              {data?.eventName}
            </Heading>

            {data?.ticketType !== "OTHER" && (
              <Button
                size="lg"
                p="15px 30px"
                borderRadius="10px"
                backgroundColor="#28A745"
                color="white"
                leftIcon={<FontAwesomeIcon icon={faTicketAlt} />} // Add ticket icon before text
                _hover={{ backgroundColor: "#218838" }}
                onClick={() => (token ? onOpen() : setCheckLogin(true))}
                mt={{ base: "20px", md: "0" }} // Adds top margin on small screens for better spacing
              >
                {t("get_tickets")}
              </Button>
            )}
          </HStack>

          {data && <Information data={data} />}

          <Box p="30px 0" borderBottom="1px solid #d4d4d4">
            <Heading fontSize="28px">{t("Description")}</Heading>
            <Text mt="10px" fontSize="18px" whiteSpace="pre-line" dangerouslySetInnerHTML={{
              __html: data?.description
                  ?.replace(/\n/g, "<br/>") // Preserve new lines
                  ?.replace(/\*(.*?)\*/g, "<strong>$1</strong>") // Make *text* bold
            }} />
          </Box>




          <Box py="50px" textAlign="center">
            <Heading fontSize="32px">{t("Attended")}</Heading>
            <HStack
              spacing={{ base: "20px", md: "80px" }}
              justifyContent="center"
              mt="30px"
            >
              <Box>
                <Text fontSize="30px" color="#66F5FF">
                  {data?.usersList?.goingCount}
                </Text>
                <Text color="gray" fontSize="20px">
                  {t("Going")}
                </Text>
              </Box>
              <Box>
                <Text fontSize="30px" color="#66F5FF">
                  {data?.usersList?.interestedCount}
                </Text>
                <Text color="gray" fontSize="20px">
                  {t("Interested")}
                </Text>
              </Box>
              <Box>
                <Text fontSize="30px" color="#66F5FF">
                  0
                </Text>
                <Text color="gray" fontSize="20px">
                  {t("Invited")}
                </Text>
              </Box>
            </HStack>
          </Box>

          <Box
            display="flex"
            flexDirection={{ base: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ base: "center", md: "flex-start" }}
            py="50px"
            gap={{ base: "30px", md: "60px" }}
          >
            <Invite />
            {data && <Contact data={data} />}
            {/* Hosted By Section */}
            {data?.userView && <Hosted data={data.userView} />}
          </Box>
        </Box>
      </Box>

      {/* Tickets Modal */}
      <Modal isOpen={isOpen} size="3xl" onClose={onClose}>
        <Tickets onClose={onClose} />
      </Modal>
    </Box>
  );
};

export default DetailView;
